import { Injectable } from '@angular/core';
import { init as initApm, ApmBase } from '@elastic/apm-rum';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment.dev';

@Injectable({
  providedIn: 'root'
})
export class ApmService {
  private apm: ApmBase | null = null;
  private isInitialized = false;

  constructor(private router: Router) {
    this.initializeApm();
  }

  /**
   * Initialize Elastic APM RUM with configuration
   */
  private initializeApm(): void {
    // Check if APM server URL is provided in environment
    if (!environment.apmServerUrl || !environment.apmServiceName) {
      console.warn('Elastic APM configuration not found. APM logging disabled.');
      return;
    }

    try {
      this.apm = initApm({
        // Required configuration
        serviceName: environment.apmServiceName,
        serverUrl: environment.apmServerUrl,
        serviceVersion: environment.apmServiceVersion || '1.0.0',
        environment: environment.environment,

        // Optional configuration
        active: true,
        instrument: true,
        disableInstrumentations: [],
        logLevel: 'warn', // 'trace' | 'debug' | 'info' | 'warn' | 'error'
        
        // Performance monitoring
        breakdownMetrics: true,
        
        // Distributed tracing
        distributedTracing: true,
        distributedTracingOrigins: [environment.apiUrl],
        
        // Page load metrics
        pageLoadTraceId: undefined,
        pageLoadSpanId: undefined,
        pageLoadSampled: undefined,
        
        // Transaction configuration
        transactionSampleRate: 1.0 // Sample 100% of transactions (adjust for production)
      });

      this.isInitialized = true;
      console.log('Elastic APM initialized successfully');

      // Set up route change tracking
      this.setupRouteTracking();
    } catch (error) {
      console.error('Failed to initialize Elastic APM:', error);
    }
  }

  /**
   * Set up automatic route change tracking
   */
  private setupRouteTracking(): void {
    if (!this.isInitialized || !this.apm) return;

    this.router.events.subscribe((event: any) => {
      if (event.constructor.name === 'NavigationEnd') {
        // Page load transactions are automatically created
        // You can add custom logic here if needed
      }
    });
  }

  /**
   * Start a custom transaction
   */
  startTransaction(name: string, type: string = 'custom'): any {
    if (this.isInitialized && this.apm) {
      return this.apm.startTransaction(name, type, {
        managed: true,
        canReuse: false
      });
    }
    return null;
  }

  /**
   * Get the current transaction
   */
  getCurrentTransaction(): any {
    if (this.isInitialized && this.apm) {
      return this.apm.getCurrentTransaction();
    }
    return null;
  }

  /**
   * Start a custom span within a transaction
   */
  startSpan(name: string, type: string = 'custom'): any {
    if (this.isInitialized && this.apm) {
      return this.apm.startSpan(name, type);
    }
    return null;
  }

  /**
   * Capture an error
   */
  captureError(error: Error | string, options?: any): void {
    if (this.isInitialized && this.apm) {
      this.apm.captureError(error);
    } else {
      console.error('APM Error:', error);
    }
  }

  /**
   * Add custom labels to the current transaction
   */
  addLabels(labels: { [key: string]: string | number | boolean }): void {
    if (this.isInitialized && this.apm) {
      const transaction = this.apm.getCurrentTransaction();
      if (transaction) {
        transaction.addLabels(labels);
      }
    }
  }

  /**
   * Add custom context to the current transaction
   */
  addContext(context: { [key: string]: any }): void {
    if (this.isInitialized && this.apm) {
      const transaction = this.apm.getCurrentTransaction();
      if (transaction) {
        transaction.addContext(context);
      }
    }
  }

  /**
   * Set user context for tracking
   */
  setUserContext(user: { id?: string; username?: string; email?: string }): void {
    if (this.isInitialized && this.apm) {
      this.apm.setUserContext(user);
    }
  }

  /**
   * Set custom context globally
   */
  setCustomContext(context: { [key: string]: any }): void {
    if (this.isInitialized && this.apm) {
      this.apm.setCustomContext(context);
    }
  }

  /**
   * Add a filter to modify or drop data before sending
   */
  addFilter(callback: (payload: any) => any): void {
    if (this.isInitialized && this.apm) {
      this.apm.addFilter(callback);
    }
  }

  /**
   * Track a user action/interaction
   */
  trackUserAction(actionName: string, type: string = 'click', context?: any): void {
    const transaction = this.startTransaction(actionName, 'user-interaction');
    if (transaction) {
      if (context) {
        transaction.addContext(context);
      }
      transaction.addLabels({ action_type: type });
      transaction.end();
    }
  }

  /**
   * Track geolocation updates
   */
  trackGeolocation(latitude: number, longitude: number, additionalData?: any): void {
    const span = this.startSpan('geolocation-update', 'app');
    if (span) {
      span.addLabels({
        latitude: latitude.toString(),
        longitude: longitude.toString()
      });
      if (additionalData) {
        span.addContext(additionalData);
      }
      span.end();
    }
  }

  /**
   * Track connectivity changes
   */
  trackConnectivityChange(isOnline: boolean, reason?: string): void {
    const transaction = this.startTransaction(
      isOnline ? 'network-online' : 'network-offline',
      'connectivity'
    );
    if (transaction) {
      transaction.addLabels({
        is_online: isOnline,
        reason: reason || 'unknown'
      });
      transaction.end();
    }
  }

  /**
   * Track app lifecycle events
   */
  trackAppLifecycle(event: 'start' | 'pause' | 'resume' | 'background', context?: any): void {
    const transaction = this.startTransaction(`app-${event}`, 'lifecycle');
    if (transaction) {
      transaction.addLabels({ lifecycle_event: event });
      if (context) {
        transaction.addContext(context);
      }
      transaction.end();
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, type: string = 'custom', context?: any, labels?: any): void {
    const transaction = this.startTransaction(name, type);
    if (transaction) {
      if (context) {
        transaction.addContext(context);
      }
      if (labels) {
        transaction.addLabels(labels);
      }
      transaction.end();
    }
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metricName: string, value: number, context?: any): void {
    const span = this.startSpan(metricName, 'performance');
    if (span) {
      span.addLabels({ metric_value: value });
      if (context) {
        span.addContext(context);
      }
      span.end();
    }
  }

  /**
   * Observe a long-running operation
   */
  observeOperation<T>(
    operationName: string,
    operation: () => Promise<T>,
    type: string = 'custom'
  ): Promise<T> {
    const transaction = this.startTransaction(operationName, type);
    
    return operation()
      .then((result) => {
        if (transaction) {
          transaction.end();
        }
        return result;
      })
      .catch((error) => {
        if (transaction) {
          this.captureError(error);
          transaction.end();
        }
        throw error;
      });
  }

  /**
   * Check if APM is initialized
   */
  isActive(): boolean {
    return this.isInitialized;
  }

  /**
   * Get the APM instance (for advanced usage)
   */
  getApmInstance(): ApmBase | null {
    return this.apm;
  }
}
